import React, { useState } from 'react';
import {
  LayoutDashboard, Bell, ShieldAlert, DollarSign, Network, Users, Ban,
  SlidersHorizontal, ScrollText, Trash2, UserCog, ShieldCheck, ChevronRight, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { API, Card, Counter, Empty, Spin, DataTable, useApi, authHeaders, fmtWhen, CustomerLink } from './SecurityShared';
import { AlertsTab, AuditTab, BlocklistTab } from './SecurityFeeds';
import { LoginsTab, AccessTab, AdminsTab } from './SecurityAccess';
import { ServiceAccountsTab } from './ServiceAccounts';
import RechargeReviewsPanel from '../admin/RechargeReviewsPanel';
import RiskLimitsPanel from '../admin/RiskLimitsPanel';
import SecurityPanel from '../admin/SecurityPanel';
import ManageIPPanel from '../ManageIPPanel';
import AdminTransactions from '../AdminTransactions';
import CustomerProfileDrawer from '../CustomerProfileDrawer';

const TABS = [
  { id: 'overview',    label: 'Overview',      icon: LayoutDashboard },
  { id: 'alerts',      label: 'Alerts',        icon: Bell },
  { id: 'reviews',     label: 'On review',     icon: ShieldAlert },
  { id: 'suspicious',  label: 'Suspicious €',  icon: DollarSign },
  { id: 'logins',      label: 'Logins',        icon: Network },
  { id: 'ip',          label: 'IP & devices',  icon: Network },
  { id: 'access',      label: 'Client access', icon: Users },
  { id: 'blocklist',   label: 'Blocklist',     icon: Ban },
  { id: 'limits',      label: 'Limits',        icon: SlidersHorizontal },
  { id: 'audit',       label: 'Action log',    icon: ScrollText },
  { id: 'deleted',     label: 'Deleted',       icon: Trash2 },
  { id: 'admins',      label: 'Admins',        icon: UserCog },
  { id: 'service',     label: 'Service keys',  icon: KeyRound },
  { id: 'auth',        label: 'Auth & MFA',    icon: ShieldCheck },
];

const LENSES = [
  { id: 'no_payment_id', label: 'Top-ups without payment ID' },
  { id: 'big',           label: 'Top-ups over €500' },
  { id: 'bonus',         label: 'All bonuses' },
  { id: 'reversal',      label: 'Refunds / reversals' },
  { id: 'admin_manual',  label: 'Manual admin adjustments' },
];

/** Overview — the first screen: counters + who logged in / signed up lately. */
function Overview({ token, go, onOpenCustomer }) {
  const { data, loading } = useApi(token, '/api/admin/security/dashboard');
  const c = data?.counters || {};

  // One-click hard block straight from the feed — the usual reaction to
  // a burst of failures from an address you do not recognise.
  const blockIp = async (ip) => {
    const reason = window.prompt(`Block ${ip}? Optional note:`, 'failed login burst');
    if (reason === null) return;
    const r = await fetch(`${API}/api/admin/security/blocklist`, {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'ip', value: ip, reason }),
    });
    if (!r.ok) { toast.error('Could not block the IP'); return; }
    toast.success(`${ip} blocked — see Blocklist`);
  };
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="sec-counters">
        <Counter testid="sec-c-reviews" label="Recharges on review" value={loading ? '…' : c.recharge_reviews_pending ?? 0}
          hint="Decide" tone={c.recharge_reviews_pending ? 'amber' : 'slate'} onClick={() => go('reviews')} />
        <Counter testid="sec-c-alerts" label="Alerts / 24 h" value={loading ? '…' : c.alerts_24h ?? 0}
          hint={`${c.alerts_unseen ?? 0} unseen`} tone={c.alerts_unseen ? 'amber' : 'slate'} onClick={() => go('alerts')} />
        <Counter testid="sec-c-limits" label="Limit hits / 24 h" value={loading ? '…' : c.rate_limit_hits_24h ?? 0}
          hint="Orders & originals" tone={c.rate_limit_hits_24h ? 'violet' : 'slate'} onClick={() => go('access')} />
        <Counter testid="sec-c-signups" label="New sign-ups / 24 h" value={loading ? '…' : c.new_users_24h ?? 0}
          hint="See the list below" tone="emerald" />
        <Counter testid="sec-c-blocked" label="Blocked accounts" value={loading ? '…' : c.blocked_accounts ?? 0}
          hint="Access denied / off" onClick={() => go('access')} />
        <Counter testid="sec-c-nopayid" label="Credits w/o payment ID" value={loading ? '…' : c.credits_without_payment_id ?? 0}
          hint="Must be 0" tone={c.credits_without_payment_id ? 'rose' : 'emerald'} onClick={() => go('suspicious')} />
        <Counter testid="sec-c-probes" label="Finance API errors / 24 h" value={loading ? '…' : c.finance_probes_24h ?? 0}
          hint="4xx / 410 probes" tone={c.finance_probes_24h ? 'amber' : 'slate'} onClick={() => go('alerts')} />
        <Counter testid="sec-c-ip" label="IP & devices" value="›" hint="Full login analytics" onClick={() => go('ip')} />
      </div>

      <Card title="Latest logins" testid="sec-recent-logins"
        hint="Newest first — spot a strange country or a failed burst without digging into sub-tabs."
        right={<button onClick={() => go('logins')} className="text-[11px] font-bold text-cyan-700 flex items-center gap-0.5"
          data-testid="sec-overview-to-logins">Risk signals <ChevronRight className="w-3 h-3" /></button>}>
        {loading ? <Spin /> : !data?.recent_logins?.length ? <Empty>No logins yet</Empty> : (
          <DataTable
            rows={data.recent_logins}
            rowTestid={(r, i) => `sec-login-row-${i}`}
            cols={[
              { key: 'cust', label: 'Customer', primary: true,
                render: (r) => <CustomerLink email={r.user_email} userId={r.user_id} onOpenCustomer={onOpenCustomer} /> },
              { key: 'res', label: 'Result', primary: true,
                render: (r) => (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.success ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    {r.success ? 'OK' : 'FAIL'}
                  </span>) },
              { key: 'when', label: 'When', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.created_at) },
              { key: 'reason', label: 'Reason', tdClass: 'text-slate-600',
                render: (r) => (r.success ? '—' : (
                  <span className="font-mono text-[11px] text-rose-700">{r.reason || 'unknown'}</span>)) },
              { key: 'client', label: 'Client', tdClass: 'text-slate-500 max-w-[200px] truncate',
                render: (r) => (
                  <span title={r.user_agent || ''}>
                    {[r.browser, r.os].filter(Boolean).join(' · ') || r.device_type || '—'}
                  </span>) },
              { key: 'ip', label: 'IP', tdClass: 'font-mono text-slate-600',
                render: (r) => (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="font-mono break-all">{r.ip || '—'}</span>
                    {r.ip && !r.success && (
                      <button onClick={() => blockIp(r.ip)} data-testid="sec-login-block-ip"
                        title="Add this IP to the blocklist"
                        className="text-[10px] font-bold text-rose-600 hover:text-rose-800 underline">block</button>
                    )}
                  </span>) },
              { key: 'country', label: 'Country', render: (r) => r.country || '—' },
            ]} />
        )}
      </Card>

      <Card title="Newest registrations (7 days)" testid="sec-recent-signups"
        hint="A fresh account placing orders immediately is the pattern we watch.">
        {loading ? <Spin /> : !data?.recent_signups?.length ? <Empty>No new accounts</Empty> : (
          <DataTable
            rows={data.recent_signups}
            rowKey={(u) => u.id}
            rowTestid={(u, i) => `sec-signup-row-${i}`}
            cols={[
              { key: 'cust', label: 'Customer', primary: true,
                render: (u, i) => (
                  <>
                    <CustomerLink email={u.email} userId={u.id} onOpenCustomer={onOpenCustomer} testid={`sec-signup-customer-${i}`} />
                    <div className="text-[11px] text-slate-500">{[u.first_name, u.surname].filter(Boolean).join(' ')}</div>
                  </>) },
              { key: 'reg', label: 'Registered', tdClass: 'text-slate-500 whitespace-nowrap', render: (u) => fmtWhen(u.registration_date) },
              { key: 'country', label: 'Country', render: (u) => u.country || '—' },
              { key: 'ip', label: 'Signup IP', tdClass: 'font-mono text-slate-600', render: (u) => u.from_ip || '—' },
              { key: 'role', label: 'Role', render: (u) => u.role },
              { key: 'trusted', label: 'Trusted', render: (u) => (u.trusted ? 'yes' : '—') },
            ]} />
        )}
      </Card>
    </>
  );
}

/** Suspicious money — the existing Transactions table with a security lens. */
function SuspiciousTab({ token }) {
  const [lens, setLens] = useState('no_payment_id');
  return (
    <>
      <Card title="Suspicious transactions" testid="sec-suspicious-card"
        hint="Same transactions list as the Transactions section, pre-filtered. Export and date filters work as usual.">
        <div className="flex flex-wrap gap-1.5">
          {LENSES.map((l) => (
            <button key={l.id} onClick={() => setLens(l.id)} data-testid={`sec-lens-${l.id}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition ${
                lens === l.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
              {l.label}
            </button>
          ))}
        </div>
      </Card>
      <AdminTransactions key={lens} token={token}
        initialFilters={{ suspicious: lens, _nonce: lens }} />
    </>
  );
}

export default function SecurityCenter({ token }) {
  const [tab, setTab] = useState('overview');
  const [customerId, setCustomerId] = useState(null);
  const go = (id) => { setTab(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const props = { token, onOpenCustomer: setCustomerId };

  return (
    <div className="p-3 sm:p-4 md:p-6 w-full max-w-[1500px] mx-auto" data-testid="security-center">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-6 h-6 text-rose-600" />
        <h1 className="text-xl md:text-2xl font-bold text-slate-900">Security</h1>
      </div>

      {/* Mobile: one native picker instead of a long pill strip */}
      <div className="lg:hidden mb-3">
        <select value={tab} onChange={(e) => go(e.target.value)} data-testid="sec-mobile-nav"
          className="w-full px-3 py-2.5 text-sm font-semibold rounded-xl bg-white border border-slate-300 shadow-sm">
          {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>

      {/* Desktop: wrapping tab strip — no horizontal scrolling hunt */}
      <div className="hidden lg:flex flex-wrap gap-1.5 mb-4" data-testid="sec-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => go(t.id)} data-testid={`sec-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition ${
                active ? 'bg-slate-900 text-white shadow' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview'   && <Overview token={token} go={go} onOpenCustomer={setCustomerId} />}
      {tab === 'alerts'     && <AlertsTab {...props} />}
      {tab === 'reviews'    && <RechargeReviewsPanel token={token} />}
      {tab === 'suspicious' && <SuspiciousTab token={token} />}
      {tab === 'logins'     && <LoginsTab {...props} />}
      {tab === 'ip'         && <ManageIPPanel token={token} />}
      {tab === 'access'     && <AccessTab {...props} />}
      {tab === 'blocklist'  && <BlocklistTab token={token} />}
      {tab === 'limits'     && <RiskLimitsPanel token={token} />}
      {tab === 'audit'      && <AuditTab token={token} />}
      {tab === 'deleted'    && <AuditTab token={token} deletionsOnly />}
      {tab === 'admins'     && <AdminsTab token={token} />}
      {tab === 'service'    && <ServiceAccountsTab token={token} />}
      {tab === 'auth'       && <SecurityPanel token={token} />}

      {customerId && (
        <CustomerProfileDrawer token={token} customerId={customerId}
          onClose={() => setCustomerId(null)} onSaved={() => {}} />
      )}
    </div>
  );
}
